import { Injectable, signal } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationExtras, ResolveStart, Router } from '@angular/router';
import { BehaviorSubject, filter, map, Observable, take } from 'rxjs';

import { EnvironmentService } from '@ecm/consumer';
import {
    Brand,
    ContextService,
    Contract,
    Portfolio,
    PortfolioService,
    Role,
    State,
    Status,
    Universe,
    UrlHelper,
} from '@ecm/portfolio';
import { LinkService, RouterHelper, RouterService, TrackingData } from '@ecm/router';

import { CARROUSEL_CURRENT_ITEM_KEY } from '../business/constants/carrousel-constant';
import { TrackedPages } from '../business/constants/tracking.constant';
import { CarrouselItemViewModelMapper } from '../business/mapper/carrousel-item-view-model-mapper';
import { CarrouselItemButtonViewModel } from '../business/model/carrousel-item-button.view-model';
import { CarrouselItemViewModel } from '../business/model/carrousel-item.view-model';
import { CarrouselOfferItemViewModel } from '../business/model/carrousel-offer-item.view-model';
import { NavDisplayConfig, SelectedLink } from './nav-display-config';

@Injectable({
    providedIn: 'root',
})
export class CarrouselStoreService {
    public isEligibleToBloom = signal(false);

    private _items$ = new BehaviorSubject<CarrouselItemViewModel[]>([]);

    private _getDisplayConfig$ = new BehaviorSubject<NavDisplayConfig>({
        lateralMenu: false,
        carrousel: false,
        selectedLink: SelectedLink.EMPTY,
    });

    private _isCustomerBenefits$ = new BehaviorSubject<boolean>(false);

    private _isOrangeServices$ = new BehaviorSubject<boolean>(false);

    private _hasInvalidContactMeans$ = new BehaviorSubject<boolean>(false);

    private _isLoggedWithOpenCaraibe$ = new BehaviorSubject<boolean>(false);

    private readonly _isVerified$ = new BehaviorSubject<boolean>(true);

    private readonly _is_bloom_activated: boolean;
    private _offerItems$ = new BehaviorSubject<CarrouselOfferItemViewModel[]>([]);

    public constructor(
        private readonly router: Router,
        private readonly linkService: LinkService,
        private readonly contextService: ContextService,
        private readonly portfolioService: PortfolioService,
        private readonly carrouselItemViewModelMapper: CarrouselItemViewModelMapper,
        private readonly routerService: RouterService,
        private readonly environmentService: EnvironmentService,
    ) {
        this._is_bloom_activated = this.environmentService.instant().FEATURES_TOGGLE.AVANTAGES.BLOOM;
    }

    public selectItem(carrouselCurrentItemId: string, isMobile = false) {
        const items = this._items$.getValue();
        const updatedItems = this.updateViewModelWithSelectedItem(items, carrouselCurrentItemId);
        this._items$.next(updatedItems);
        const updatedOfferItems = updatedItems.find((item) => item.isSelected)?.offerItems ?? [];
        this.updateContextMobile(updatedOfferItems, isMobile);
        this._offerItems$.next(updatedOfferItems);

        if (!carrouselCurrentItemId.includes('gerer-profils')) {
            this.routerService.navigate(['accueil'], this.extractParams(carrouselCurrentItemId));
        }
    }

    public openLink(button: CarrouselItemButtonViewModel): void {
        const pageTitle = button.label.toLowerCase().includes('multi-sim')
            ? TrackedPages.EC_LOGGED
            : TrackedPages.DASHBOARD;
        const trackingData = new TrackingData();
        trackingData.tmsTracking = {
            trackingArea: button.trackingArea,
            trackingName: button.trackingName || button.label,
            targetUrl: button.hrefLink,
            trackedPage: pageTitle,
            additionalProperties: button.additionalProperties,
        };

        this.linkService.openLink(button.link, trackingData, button.params, button.navigationExtras);
    }

    //////////////////////
    //  QUERIES
    //////////////////////

    public get items$(): Observable<CarrouselItemViewModel[]> {
        return this._items$.asObservable();
    }

    public get getDisplayConfig$(): Observable<NavDisplayConfig> {
        return this._getDisplayConfig$.asObservable();
    }

    public get checkAncrageCustomerBenefits$(): Observable<boolean> {
        return this._isCustomerBenefits$.asObservable();
    }

    public get checkAncrageOrangeServices$(): Observable<boolean> {
        return this._isOrangeServices$.asObservable();
    }

    public get gethasInvalidContactMeans$(): Observable<boolean> {
        return this._hasInvalidContactMeans$.asObservable();
    }

    public get getIsLoggedWithOpenCaraibe$(): Observable<boolean> {
        return this._isLoggedWithOpenCaraibe$.asObservable();
    }

    public get getIsVerified$(): Observable<boolean> {
        return this._isVerified$.asObservable();
    }

    public get getOfferItems$(): Observable<CarrouselOfferItemViewModel[]> {
        return this._offerItems$.asObservable();
    }

    //////////////////////
    //  PRIVATE
    //////////////////////

    public listenRouterEvents(): void {
        this.router.events
            .pipe(
                filter((e): e is ResolveStart => e instanceof ResolveStart),
                map((e: ResolveStart) => {
                    // hack, if a cid is present in the url we want to retrive the member/contrat from this id
                    const cidFromUrl = UrlHelper.getCidFromUrl(e.urlAfterRedirects);

                    return {
                        encodedCurrentItemId: cidFromUrl ? `cid:${cidFromUrl}` : this.getEncodedCurrentItemId(e),
                        carrouselConfig: RouterHelper.getRecursiveStates(e.state?.root).reduce(
                            (accumulator: NavDisplayConfig, snapshot: ActivatedRouteSnapshot) => ({
                                lateralMenu:
                                    accumulator.lateralMenu ||
                                    snapshot.data.lateralMenu === true ||
                                    snapshot.data.carrousel === true,
                                carrousel: accumulator.carrousel || snapshot.data.carrousel === true,
                                selectedLink: accumulator.selectedLink || snapshot.data.selectedLink || '',
                            }),
                            {
                                lateralMenu: false,
                                carrousel: false,
                                selectedLink: SelectedLink.EMPTY,
                            } as NavDisplayConfig,
                        ),
                    };
                }),
            )
            .subscribe(({ encodedCurrentItemId, carrouselConfig }) => {
                // Retrieve data from session storage in case url param is not provided
                const encodedStoredItemId = window.sessionStorage.getItem(CARROUSEL_CURRENT_ITEM_KEY);
                if (carrouselConfig.lateralMenu || carrouselConfig.carrousel) {
                    // only dashboard has carrousel to true, we use this data to change the way we load items
                    this.loadItems(encodedCurrentItemId, encodedStoredItemId, carrouselConfig.carrousel);
                }
                this._getDisplayConfig$.next(carrouselConfig);
            });
    }

    /** Returns encodedQueryParamItemId if provided, or the encodedCurrentRouteItemId otherwise */
    private getEncodedCurrentItemId(event: ResolveStart): string {
        const encodedQueryParamItemId = event.state.root.queryParams[CARROUSEL_CURRENT_ITEM_KEY];
        const encodedCurrentRouteItemId = btoa(event.urlAfterRedirects);
        return encodedQueryParamItemId || encodedCurrentRouteItemId;
    }

    private loadItems(encodedCurrentItemId?: string, encodedStoredItemId?: string, calledFromDashboard = false): void {
        let isAC = false;
        this.portfolioService
            .getPortfolio(null, calledFromDashboard)
            .pipe(take(1))
            .subscribe((portfolio: Portfolio) => {
                // Update carrousel items
                const carrouselItems = this.carrouselItemViewModelMapper.getCarrouselItems(portfolio);
                carrouselItems.forEach((item) => {
                    this.updateButtonHref(item.button);
                });
                // hack, we get the cid with a different format so we can find the corresponding member and generate a context ID from it
                if (encodedCurrentItemId.startsWith('cid:')) {
                    const cid = encodedCurrentItemId.slice(4);
                    encodedCurrentItemId = btoa(cid);
                }
                const updatedItems = this.updateViewModelWithSelectedItem(
                    carrouselItems,
                    this.getItemIdToSelect(
                        encodedCurrentItemId,
                        encodedStoredItemId,
                        carrouselItems,
                        portfolio,
                        calledFromDashboard,
                    ),
                );

                this._items$.next(updatedItems);
                const updatedOfferItems = updatedItems.find((item) => item.isSelected)?.offerItems ?? [];
                this._offerItems$.next(updatedOfferItems);
                const contracts = portfolio.contracts.filter(
                    (contract: Contract) =>
                        contract.brand.toUpperCase() === Brand.ORANGE &&
                        contract.role.toUpperCase() === Role.TITULAIRE &&
                        contract.status?.toUpperCase() === Status.ACTIF &&
                        contract.telco?.state?.toUpperCase() != State.VIM_ELIGIBLE &&
                        (['internet', 'open', '4ghome'].includes(contract.telco?.subType) ||
                            (contract.telco?.subType === Universe.MOBILE && contract.telco?.type === 'mobilePostpaid')),
                );
                isAC = contracts.length > 0;
                this._isCustomerBenefits$.next(isAC);

                const contractsServices = portfolio.contracts.filter(
                    (contract: Contract) =>
                        contract.brand.toUpperCase() === Brand.ORANGE &&
                        contract.role.toUpperCase() === Role.TITULAIRE &&
                        contract.status?.toUpperCase() === Status.ACTIF &&
                        contract.telco?.subType?.toLowerCase() !== Universe.PARNASSE &&
                        contract.telco?.state?.toUpperCase() !== State.VIM_ELIGIBLE &&
                        !['OBS', 'PRO'].includes(contract.telco?.marketType),
                );
                const contractsServicesMetro = portfolio.contracts.filter(
                    (contract: Contract) =>
                        contract.telco?.state?.toUpperCase() === State.LOGGED &&
                        (!contract.contractInstallationArea ||
                            contract.contractInstallationArea?.geographicalArea?.toUpperCase() === 'METRO'),
                );

                if (portfolio.identity) {
                    isAC =
                        contractsServices.length > 0 &&
                        contractsServicesMetro.length > 0 &&
                        !portfolio.identity.isLoggedWithOpenCaraibe;
                    this._isOrangeServices$.next(isAC);

                    if (portfolio.identity.hasInvalidContactMeans) {
                        this._hasInvalidContactMeans$.next(true);
                    }

                    if (portfolio.identity.isLoggedWithOpenCaraibe) {
                        this._isLoggedWithOpenCaraibe$.next(true);
                    }
                    if (portfolio.identity.isEligibleToBloom && this._is_bloom_activated) {
                        this.isEligibleToBloom.set(true);
                    }

                    this._isVerified$.next(portfolio.identity.isVerified ?? true);
                }
            });
    }

    private getItemIdToSelect(
        encodedCurrentItemId: string,
        encodedStoredItemId: string,
        carrouselItems: CarrouselItemViewModel[],
        portfolio: Portfolio,
        selectFirstItemByDefault: boolean,
    ): string {
        // Decode encoded item ids
        const decodedCurrentItemId = this.decodeItemId(encodedCurrentItemId);
        const decodedStoredItemId = this.decodeItemId(encodedStoredItemId);

        // Check if item ids are eligible to be selected
        const currentItemId = portfolio.contracts.filter(
            (item) =>
                item.cid === decodedCurrentItemId ||
                item.associatedFamilyMembers?.some((member) => member.id === decodedCurrentItemId),
        )[0]?.cid;

        const storedItemId = portfolio.contracts.filter(
            (item) =>
                item.cid === decodedStoredItemId ||
                item.associatedFamilyMembers?.some((member) => member.id === decodedStoredItemId),
        )[0]?.cid;

        const isStoredItemIdRelevant = this.isStoredItemRelevant(decodedCurrentItemId, decodedStoredItemId);
        // Selection is based on current id if provided, else stored id if provided, else first item otherwise
        if (selectFirstItemByDefault) {
            this._offerItems$.next(carrouselItems?.[0]?.offerItems);
        }
        return (
            currentItemId ||
            (isStoredItemIdRelevant && storedItemId) ||
            (selectFirstItemByDefault && carrouselItems?.[0]?.offerItems?.[0]?.cid)
        );
    }

    private decodeItemId(encodedItemId: string): string {
        let decodedItemId;
        try {
            decodedItemId = encodedItemId ? atob(encodedItemId) : undefined;
        } catch (error) {
            decodedItemId = undefined;
        }
        return decodedItemId;
    }

    /** An item ID is relevant if defined but not as a link or equal to current route */
    private isStoredItemRelevant(currentItemId: string, storedItemId: string): boolean {
        return (storedItemId && !storedItemId.startsWith('/')) || storedItemId === currentItemId;
    }

    private updateViewModelWithSelectedItem(
        items: CarrouselItemViewModel[],
        selectedItemId: string,
    ): CarrouselItemViewModel[] {
        // sets the selected item id in a sort of a store and get it back in dashboard container init and in tms service
        this.contextService.updateContextId(selectedItemId);

        // store selected item in session storage and is used above to retrieve it on router events
        if (selectedItemId) {
            window.sessionStorage.setItem(CARROUSEL_CURRENT_ITEM_KEY, btoa(selectedItemId));
        }

        return items
            .map((item) => {
                // offer item selection based on the ID passed at selection (click)
                return {
                    ...item,
                    offerItems: item?.offerItems?.map((offerItem) => {
                        return { ...offerItem, isSelected: offerItem.cid === selectedItemId };
                    }),
                };
            })
            .map((item) => ({
                // item selection based on the ID passed at selection (click)
                ...item,
                isSelected: item?.offerItems?.some((offerItem) => offerItem.isSelected) || item.id === selectedItemId,
            }))
            .map((item) => {
                //selection of the specific offerItem
                const hasOnlyOneOffer = item.offerItems?.length === 1;
                item = {
                    ...item,
                    offerItems: !item.isSelected
                        ? item?.offerItems
                        : item?.offerItems?.map((offerItem, i, offerItems) => ({
                              ...offerItem,
                              isSelected:
                                  hasOnlyOneOffer ||
                                  offerItem.cid === selectedItemId ||
                                  (i === 0 && !offerItems.some((item) => item.isSelected)),
                          })),
                };
                return item;
            });
    }

    private updateContextMobile(items: CarrouselOfferItemViewModel[], isMobile: boolean): void {
        let contextId;
        if (isMobile) {
            contextId = items.find((item) => item.isSelected)?.cid;
            window.sessionStorage.setItem(CARROUSEL_CURRENT_ITEM_KEY, btoa(contextId));
            this.contextService.updateContextId(contextId);
        }
    }

    private updateButtonHref(button: CarrouselItemButtonViewModel): void {
        button.hrefLink = this.linkService.getLinkSync(button.link, button.params);
    }

    private extractParams(itemId: string): NavigationExtras {
        const queryParams = {};
        if (itemId) {
            queryParams[CARROUSEL_CURRENT_ITEM_KEY] = btoa(itemId);
        }

        return {
            queryParams: queryParams,
        };
    }
}
